import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { m } from '@/lib/motion';
import { KeyRound, Mail } from 'lucide-react';
import { fadeIn, slideInUp } from '@/lib/motion-variants';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SegmentedToggle } from '@/components/ui/SegmentedToggle';

export interface MfaCodeStepProps {
  email: string;
  methods: Array<'email' | 'totp' | 'recovery'>;
  preferred?: 'email' | 'totp';
  onSubmit: (args: { code: string; method: 'email' | 'totp' | 'recovery' }) => Promise<void>;
  onBack: () => void;
  onResend: () => Promise<void>;
  isSubmitting: boolean;
  isResending: boolean;
}

export function MfaCodeStep({
  email,
  methods,
  preferred,
  onSubmit,
  onBack,
  onResend,
  isSubmitting,
  isResending,
}: MfaCodeStepProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [method, setMethod] = useState<'email' | 'totp' | 'recovery'>(() => {
    if (preferred === 'totp' && methods.includes('totp')) return 'totp';
    if (methods.includes('totp')) return 'totp';
    if (methods.includes('email')) return 'email';
    return 'recovery';
  });
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{1,6}$/.test(code.replace(/\D/g, ''))) {
      setError(t('login.mfaCodeInvalid'));
      return;
    }
    setError(undefined);
    try {
      const digits = code.replace(/\D/g, '');
      const normalized = digits.length > 6 ? digits.slice(-6) : digits.padStart(6, '0');
      await onSubmit({ code: normalized, method });
    } catch {
      // Toast from mutation
    }
  };

  return (
    <main className='min-h-screen bg-gradient-to-br from-dark-500 via-dark-500 to-dark-600 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4 sm:items-center items-start pt-16 sm:pt-4'>
      <div className='w-full max-w-md'>
        <m.div {...fadeIn} className='text-center mb-10'>
          <img src='/xkey-clean.png' alt='Xkey logo' height='60' width='108' className='mx-auto mb-6 drop-shadow-lg' />
        </m.div>

        <m.div
          {...slideInUp}
          className='bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-200/20 dark:border-gray-700/30 p-8 md:p-10'
        >
          <div className='text-center mb-6'>
            <h2 className='text-2xl font-bold text-foreground mb-2'>{t('login.mfaHeader')}</h2>
            <p className='text-sm text-muted-strong'>{t('login.mfaDescription')}</p>
            <p className='text-xs font-mono text-muted mt-2 flex items-center justify-center gap-1.5'>
              <Mail className='h-3.5 w-3.5 shrink-0' />
              {email}
            </p>
            <div className='h-1 w-16 bg-primary-500 mx-auto rounded-full mt-4' />
          </div>

          {methods.length > 1 && (
            <div className='mb-5 flex justify-center'>
              <SegmentedToggle<'email' | 'totp' | 'recovery'>
                value={method}
                onChange={(next) => setMethod(next)}
                ariaLabel={t('general.select')}
                className='w-full max-w-[320px] border-b-2 border-b-gray-400/90 dark:border-b-gray-500/90'
                options={[
                  ...(methods.includes('totp')
                    ? [
                        {
                          value: 'totp' as const,
                          label: t('login.mfaMethodTotp'),
                          disabled: isSubmitting || isResending,
                        },
                      ]
                    : []),
                  ...(methods.includes('email')
                    ? [
                        {
                          value: 'email' as const,
                          label: t('login.mfaMethodEmail'),
                          disabled: isSubmitting || isResending,
                        },
                      ]
                    : []),
                  ...(methods.includes('recovery')
                    ? [
                        {
                          value: 'recovery' as const,
                          label: t('login.mfaMethodRecovery'),
                          disabled: isSubmitting || isResending,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className='space-y-5'>
            <Input
              ref={inputRef}
              label={t('login.mfaCodeLabel')}
              inputMode='numeric'
              autoComplete='one-time-code'
              name='mfaCode'
              placeholder='000000'
              value={code}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(v);
                if (error) setError(undefined);
              }}
              maxLength={8}
              leftIcon={<KeyRound className='h-4 w-4' />}
              error={error}
              disabled={isSubmitting}
              className='bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-700 transition-all duration-200'
            />
            <Button
              type='submit'
              className='w-full h-12 text-base font-semibold'
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('login.mfaSubmit')}
            </Button>
          </form>

          <div className='mt-4 grid grid-cols-1 gap-2 text-sm'>
            <Button type='button' variant='outline' onClick={onBack} disabled={isSubmitting} fullWidth>
              {t('login.mfaBack')}
            </Button>
            {method === 'email' && (
              <Button
                type='button'
                variant='outline'
                onClick={() => onResend().catch(() => undefined)}
                loading={isResending}
                disabled={isSubmitting || isResending}
                fullWidth
              >
                {t('login.mfaResend')}
              </Button>
            )}
          </div>
        </m.div>
      </div>
    </main>
  );
}
